import TodaysCollectionKpi from "./TodaysCollectionKpi";
import YesterdayCollectionKpi from "./YesterdayCollectionKpi";
import PettyCashBalanceKpi from "./PettyCashBalanceKpi";
import PendingLposKpi from "./PendingLposKpi";
import ClientRequestsKpi from "./ClientRequestsKpi";
import DailyCollectionByBranchChart from "./DailyCollectionByBranchChart";
import ClientRequestsOverviewChart from "./ClientRequestsOverviewChart";
import LpoSummaryCard from "./LpoSummaryCard";
import PettyCashByBranchCard from "./PettyCashByBranchCard";
import PettyCashTransactionsTodayCard from "./PettyCashTransactionsTodayCard";
import RecentLposCard from "./RecentLposCard";
import CollectionTrendChart from "./CollectionTrendChart";
import TopClientsByCollectionCard from "./TopClientsByCollectionCard";
import AdministrationQuickLinksWidget from "./AdministrationQuickLinksWidget";
import CollectionsDashboardSection from "./collections/CollectionsDashboardSection";
import AmcServiceDueCard from "./AmcServiceDueCard";
import AmcRenewalsDueCard from "./AmcRenewalsDueCard";
import AttendanceTodayCard from "./AttendanceTodayCard";
import GuardedCustomersMissedPaymentsCard from "./GuardedCustomersMissedPaymentsCard";
import InstallationOrdersVsCollectedCard from "./InstallationOrdersVsCollectedCard";

/**
 * The dashboard's entire content is this ordered list - nothing else in the
 * app decides what appears or where. Every widget is a fully self-contained
 * component: it owns its permission check, its own data fetching, and its
 * own grid-column sizing (the `col-*` class lives on the widget's own root
 * element, not here), so it renders `null` outright - not an empty gapped
 * column - when the caller can't see it or there's nothing to show.
 *
 * To add a widget: write the component (copy an existing one of a similar
 * shape as a starting point - KPI tile / chart card / list card), then add
 * one line here. To remove one: delete its line. To rearrange: reorder this
 * array. No other file needs to change for any of the three.
 */
export const DASHBOARD_WIDGETS = [
  // KPI row
  { id: "todays-collection", Component: TodaysCollectionKpi },
  { id: "yesterday-collection", Component: YesterdayCollectionKpi },
  { id: "pettycash-balance", Component: PettyCashBalanceKpi },
  { id: "pending-lpos", Component: PendingLposKpi },
  { id: "client-requests", Component: ClientRequestsKpi },
  // Sits in the KPI row itself, immediately after Client Requests - so it lands
  // on the second line of tiles, beside Client Requests and under Yesterday's
  // Collection. Requested there by name: attendance is a start-of-shift figure
  // people check first, not something to scroll to.
  { id: "attendance-today", Component: AttendanceTodayCard },

  // AMC is deliberately high on the page, directly under the KPI row: both cards
  // are time-sensitive by nature (a service goes from due to critical, a contract
  // from expiring to expired, on their own, with nobody touching anything), unlike
  // everything below them which reports on what people have already done.
  { id: "amc-service-due", Component: AmcServiceDueCard },
  { id: "amc-renewals-due", Component: AmcRenewalsDueCard },

  // Ported from the Flutter app's Dashboard Collections card - collapsible,
  // per-branch expand-to-see-detail, own full-width row.
  { id: "collections-section", Component: CollectionsDashboardSection },

  // Charts / summary cards
  { id: "daily-collection-by-branch", Component: DailyCollectionByBranchChart },
  { id: "client-requests-overview", Component: ClientRequestsOverviewChart },
  { id: "lpo-summary", Component: LpoSummaryCard },

  { id: "pettycash-by-branch", Component: PettyCashByBranchCard },
  { id: "pettycash-transactions-today", Component: PettyCashTransactionsTodayCard },
  { id: "recent-lpos", Component: RecentLposCard },

  { id: "collection-trend", Component: CollectionTrendChart },
  { id: "top-clients-by-collection", Component: TopClientsByCollectionCard },
  // Guard posts (Attendance) joined to monthly collections - who has guards on
  // site but no payment entry for a month. Beside Top Clients: same subject
  // (customers and their money), opposite end of the list.
  { id: "guarded-customers-missed-payments", Component: GuardedCustomersMissedPaymentsCard },
  // The one-time counterpart of the card above: new-installation order value
  // against Installation-tagged receipts. Kept a separate card on purpose -
  // monthly charges and one-off bills are never added together.
  { id: "installation-orders-vs-collected", Component: InstallationOrdersVsCollectedCard },

  // Always last - unrelated to the operational widgets above
  { id: "admin-quick-links", Component: AdministrationQuickLinksWidget },
];
